import { Language } from '@pai/commons'

import { Links } from 'app/core/cms/cms.service'

import {
  LexicalCodeNode,
  LexicalHeadingNode,
  LexicalLinkNode,
  LexicalListItemNode,
  LexicalListNode,
  LexicalNode,
  LexicalParagraphNode,
  LexicalQuoteNode,
  LexicalRichText,
  LexicalTextNode,
} from './lexical.type'

const LEXICAL_TEXT_FORMAT = {
  BOLD: 1,
  ITALIC: 2,
  STRIKETHROUGH: 4,
  UNDERLINE: 8,
  CODE: 16,
}

export const RT_LINK_TYPE_ATTR = 'data-rt-link'

function escapeHtml(unsafe: string): string {
  return unsafe
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

function hasFormat(format: number, flag: number): boolean {
  return (format & flag) !== 0
}

function applyTextFormat(text: string, format: number): string {
  let result = text
  if (hasFormat(format, LEXICAL_TEXT_FORMAT.BOLD)) {
    result = `<b>${result}</b>`
  }
  if (hasFormat(format, LEXICAL_TEXT_FORMAT.ITALIC)) {
    result = `<i>${result}</i>`
  }
  if (hasFormat(format, LEXICAL_TEXT_FORMAT.UNDERLINE)) {
    result = `<u>${result}</u>`
  }
  if (hasFormat(format, LEXICAL_TEXT_FORMAT.STRIKETHROUGH)) {
    result = `<del>${result}</del>`
  }
  if (hasFormat(format, LEXICAL_TEXT_FORMAT.CODE)) {
    result = `<code>${result}</code>`
  }
  return result
}

function renderTextNode(node: LexicalTextNode): string {
  const text = escapeHtml(node.text)
  return applyTextFormat(text, node.format)
}

function renderLinkNode(
  node: LexicalLinkNode,
  currentLang: Language,
  links: { [id: string]: `${Language}/${string}` },
): string {
  const innerHtml = node.children.map((child) => renderNode(child, currentLang, links)).join('')

  let href: string
  let linkType: string

  if (node.fields.linkType === 'custom' && node.fields.url) {
    href = node.fields.url
    linkType = node.fields.url.startsWith('mailto:') ? 'email' : 'url'
  } else if (node.fields.linkType === 'internal' && node.fields.doc) {
    const docId = node.fields.doc.value
    const path = links[docId]
    href = path ? path : '#'
    linkType = 'internal'
  } else {
    href = '#'
    linkType = 'url'
  }

  const props: Record<string, string> = {
    [RT_LINK_TYPE_ATTR]: linkType,
    href,
  }

  if (node.fields.newTab) {
    props.target = '_blank'
  }

  if (node.fields.doc?.label) {
    props['aria-label'] = node.fields.doc.label
  }

  const propsStr = Object.entries(props)
    .map(([k, v]) => `${k}="${v}"`)
    .join(' ')

  return `<a ${propsStr}>${innerHtml}</a>`
}

function renderNode(
  node: LexicalNode,
  currentLang: Language,
  links: { [id: string]: `${Language}/${string}` },
): string {
  switch (node.type) {
    case 'text':
      return renderTextNode(node as LexicalTextNode)

    case 'linebreak':
      return '<br>'

    case 'paragraph': {
      const paragraphNode = node as LexicalParagraphNode
      const innerHtml = paragraphNode.children.map((child) => renderNode(child, currentLang, links)).join('')
      return innerHtml ? `<p>${innerHtml}</p>` : ''
    }

    case 'heading': {
      const headingNode = node as LexicalHeadingNode
      const innerHtml = headingNode.children.map((child) => renderNode(child, currentLang, links)).join('')
      return innerHtml ? `<${headingNode.tag}>${innerHtml}</${headingNode.tag}>` : ''
    }

    case 'list': {
      const listNode = node as LexicalListNode
      const innerHtml = listNode.children.map((child) => renderNode(child, currentLang, links)).join('')
      return innerHtml ? `<${listNode.tag}>${innerHtml}</${listNode.tag}>` : ''
    }

    case 'listitem': {
      const listItemNode = node as LexicalListItemNode
      const innerHtml = listItemNode.children.map((child) => renderNode(child, currentLang, links)).join('')
      return innerHtml ? `<li>${innerHtml}</li>` : ''
    }

    case 'quote': {
      const quoteNode = node as LexicalQuoteNode
      const innerHtml = quoteNode.children.map((child) => renderNode(child, currentLang, links)).join('')
      return innerHtml ? `<blockquote>${innerHtml}</blockquote>` : ''
    }

    case 'code': {
      const codeNode = node as LexicalCodeNode
      const innerHtml = codeNode.children.map((child) => renderNode(child, currentLang, links)).join('')
      return innerHtml ? `<pre><code>${innerHtml}</code></pre>` : ''
    }

    case 'link':
      return renderLinkNode(node as LexicalLinkNode, currentLang, links)

    default:
      return ''
  }
}

export function lexicalToHtml(richText: LexicalRichText, currentLang: Language, links: Links): string {
  return richText.root.children.map((child) => renderNode(child, currentLang, links)).join('')
}
